import { FC, useEffect, useRef, useState } from "react";
import { KTSVG } from "../../_metronic/helpers";
import { useFormik } from "formik";
import * as Yup from 'yup';
import Swal from "sweetalert2";
import clsx from "clsx";
import useAccessToken from "../../hooks/AccessToken";
import useSWR, { mutate } from "swr";
import { SelectOption } from "../../interfaces/SelectOption";
import { handleCaps } from "../../functions/general";
import { useIntl } from "react-intl";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import PhoneInput from "react-phone-input-2";
import { checkWhatsapp } from "../../api/Whatsapp";
import VirtualizedSelect, { VirtualizedSelectOption } from "../Select/SelectVirtualized";
import axios from "axios";
import { Agent } from "../../interfaces/Agent";
import { updateAgent } from "../../api/AgentCRUD";
import useModalEffect from "../../hooks/useModalEffect";
import { OTPModal } from "../OTPModal";
import useUser from "../../hooks/User";

interface Props {
    agent: Agent
}

const UpdateAgent: FC<Props> = ({ agent }) => {
    const API_URL = process.env.REACT_APP_API_URL
    const intl = useIntl()
    const token = useAccessToken()
    const user = useUser()

    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [modalBtnLoading, setModalBtnLoading] = useState(false)
    const [isOtpChatPhoneModal, setIsOtpChatPhoneModal] = useState<boolean>(false);
    const [checkWa, setCheckWa] = useState("")
    const [listUsers, setListUsers] = useState<SelectOption[]>([]);
    const [requestId, setRequestId] = useState('');

    const fetcher = async (url: string) => {
        const response = await axios.get(url, {
            headers: {
                'X-Auth-token': token,
            },
        });
        return response.data.data;
    };

    const { data: dataUsers = [], isLoading: isLoadingDataUsers } = useSWR(`${API_URL}user/company`, fetcher)

    let initialValues = {
        name: agent.label,
        phone: agent.phone,
        agent: agent.agents.map((agent) => agent.id)
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: Yup.object().shape({
            name: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            phone: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PHONE" }) })),
            agent: Yup.array().min(1, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.AGENT" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.AGENT" }) })).nullable()
        }),
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)

            try {
                const response = await updateAgent(agent.id, values.phone, values.name, values.agent, token)
                if (response.status === 200) {
                    setRequestId(response.data.data.requestId)
                    setIsOtpChatPhoneModal(true)
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    heightAuto: false,
                    title: error.response.data.message,
                    confirmButtonText: 'Ok'
                })
            } finally {
                setModalBtnLoading(false)
            }
        }
    });

    // const checkWaValid = async (phone: string) => {
    //     setCheckWa("loading")
    //     try {
    //         const response = await checkWhatsapp(phone.replace("+", ""))
    //         setCheckWa(response.data.result.exists ? "true" : "false")
    //         return response.data.result.exists ? "true" : "false";
    //     } catch (error) {

    //     }
    // }

    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listener
        const handleModalHidden = () => {
            formik.resetForm()
            setCheckWa("")
        };

        // Attach the event listener
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);


    useEffect(() => {
        if (!isLoadingDataUsers) {
            let data = dataUsers.map((user: any) => ({
                ...user,
                label: user.name,
                value: user.id
            }))

            setListUsers(data)
        }
    }, [dataUsers])

    useModalEffect({
        showModal: isOtpChatPhoneModal,
        setShowModal: setIsOtpChatPhoneModal,
        modalId: `otp-modal-settingAgent`
    })

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <>
            <div className="modal fade" tabIndex={-1} id="update-agent-modal" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })} {agent.label}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                <div>
                                    <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.NAME' })}</label>
                                    <div className="input-group mb-3">
                                        <input
                                            placeholder={intl.formatMessage({ id: 'FORM.LABEL.NAME' })}
                                            {...formik.getFieldProps('name')}
                                            className={clsx(
                                                'form-control form-control-lg',
                                                { 'is-invalid': formik.touched.name && formik.errors.name },
                                            )}
                                            type='text'
                                            name='name'
                                            autoComplete='off'
                                            onInput={(e) => handleCaps(e)}
                                        />
                                    </div>
                                    {formik.touched.name && formik.errors.name && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.name}</span>
                                        </div>
                                    )}

                                    {/* <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.PHONE' })}</label>
                                    {
                                        checkWa === "true" ?
                                            <span className="text-success" style={{ float: "inline-end" }}>{intl.formatMessage({ id: "FORM.VALIDATION.VALID_WHATSAPP" })}</span>
                                            : checkWa === "false" ?
                                                <span className="text-danger" style={{ float: "inline-end" }}>{intl.formatMessage({ id: "FORM.VALIDATION.INVALID_WHATSAPP" })}</span>
                                                : checkWa === "loading" ?
                                                    <span className="align-middle spinner-border spinner-border-sm" style={{ float: "inline-end" }}></span>
                                                    : <></>
                                    }
                                    <div style={{ marginBottom: "0.75rem" }}>
                                        <PhoneInput
                                            countryCodeEditable={false}
                                            country="id"
                                            value={formik.values.phone}
                                            onChange={(value, country: any) => {
                                                formik.setFieldValue('phone', value);
                                            }}
                                            onBlur={() => checkWaValid(formik.values.phone)}
                                            enableSearch={true}
                                            inputStyle={{ width: "100%" }}
                                            dropdownClass="z-index-3"
                                        />
                                    </div>
                                    {formik.touched.phone && formik.errors.phone && (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{formik.errors.phone}</span>
                                        </div>
                                    )} */}

                                    <label className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.AGENT' })}</label>
                                    <VirtualizedSelect
                                        options={listUsers}
                                        isLoading={isLoadingDataUsers}
                                        isDisabled={isLoadingDataUsers}
                                        className="w-100"
                                        placeholder={`${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: 'FORM.LABEL.AGENT' })}`}
                                        value={listUsers?.filter(option => (formik.values['agent'] as string[])?.includes(option.value)) as VirtualizedSelectOption[]}
                                        onChange={(selectedOptions) =>
                                            formik.setFieldValue(
                                                'agent',
                                                selectedOptions ? selectedOptions.map((option: VirtualizedSelectOption) => option.value) : []
                                            )
                                        }
                                        onBlur={() => {
                                            if (formik.values['agent'].length === 0) {
                                                formik.setFieldTouched('agent', true);
                                            }
                                        }}
                                        isMulti
                                    />
                                    {formik.touched.agent && formik.errors.agent && (
                                        <div className='text-danger fv-plugins-message-container'>
                                            <span role='alert' className="text-danger">{formik.errors.agent}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? 'disabled' : ''}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {
                isOtpChatPhoneModal && <OTPModal username={user.data.username} password="" phone={formik.values.phone} type={"settingAgent"} requestId={requestId} data={{ name: formik.values.name, agent: formik.values.agent }} closeModalRef={closeModalRef} />
            }
        </>
    )
}

export { UpdateAgent }